/**
 * Coverage Explorer — a second page (coverage.html) off the main viewer.
 *
 *  - A US map with coverage dots (binned centroid density) for the current
 *    tab + filters. Clicking the map opens the viewer centered there.
 *  - A tabbed, filterable, sortable list of EVERY catalog layer (Aerial /
 *    Historic Map / USGS Topo). Each row links into the viewer on that layer.
 *
 * No catalog changes: kind is derived from category/id, and resolution is
 * estimated from max_zoom + latitude (156543·cos(lat)/2^z m/px) — the same
 * served-pixel-scale proxy used elsewhere.
 */
import L from "leaflet";
import { loadCatalog, type CatalogLayer } from "./catalog";

type Kind = "aerial" | "map" | "topo";
const KIND_COLOR: Record<Kind, string> = { aerial: "#4a9eff", map: "#e8823a", topo: "#46b26a" };
const KIND_LABEL: Record<Kind, string> = { aerial: "Aerial", map: "Historic Map", topo: "USGS Topo" };

interface Row {
  layer: CatalogLayer;
  kind: Kind;
  lat: number;
  lng: number;
  year: number | null;
  resM: number | null; // m/px, or null if unknown
  fitZoom: number;
  hay: string; // lowercased searchable text (title)
}

function classify(l: CatalogLayer): Kind | null {
  if (l.category === "imagery") return "aerial";
  // Topos: the ~12.8k USGS HTMC quads (htmc- prefix), plus any scale-titled quad.
  if (l.id.startsWith("htmc-") || /\b1:\d{4,}|quadrangle/i.test(String(l.title ?? ""))) return "topo";
  if (l.category === "historical") return "map";
  return null; // reference / base layers — skip
}

function fitZoom(b: [[number, number], [number, number]]): number {
  const [[s, w], [n, e]] = b;
  const lngSpan = Math.abs(e - w) || 0.02;
  const latSpan = Math.abs(n - s) || 0.02;
  const zw = Math.log2((360 / lngSpan) * (900 / 256));
  const zh = Math.log2((170 / latSpan) * (560 / 256));
  return Math.max(3, Math.min(17, Math.floor(Math.min(zw, zh))));
}

function resMeters(l: CatalogLayer, lat: number): number | null {
  const mz = l.max_zoom;
  if (mz == null) return null;
  return (156543.03 * Math.cos((lat * Math.PI) / 180)) / 2 ** mz;
}

function fmtRes(r: number | null): string {
  if (r == null) return "—";
  if (r < 1) return `${Math.round(r * 100)} cm/px`;
  return `${r.toFixed(1)} m/px`;
}

function viewerUrlLayer(r: Row): string {
  return `/#ll=${r.lat.toFixed(5)},${r.lng.toFixed(5)},${r.fitZoom}&layers=${r.layer.id}:1.00`;
}
function viewerUrlArea(lat: number, lng: number, z = 13): string {
  return `/#ll=${lat.toFixed(5)},${lng.toFixed(5)},${z}`;
}

// ---- state ----
let ROWS: Row[] = [];
let tab: Kind = "aerial";
let search = "";
let yearMin: number | null = null;
let yearMax: number | null = null;
let sortKey: "title" | "year" | "resM" = "year";
let sortDir: 1 | -1 = -1; // default: newest first
const ROW_CAP = 500;

// ---- map ----
let map: L.Map;
let dotLayer: L.LayerGroup;

function buildMap() {
  map = L.map("map", { minZoom: 3, maxZoom: 16, zoomControl: true }).setView([39.5, -98.35], 4);
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: "© OpenStreetMap contributors", subdomains: "abc", maxZoom: 19,
  }).addTo(map);
  dotLayer = L.layerGroup().addTo(map);
  map.on("click", (e: L.LeafletMouseEvent) => {
    const z = Math.min(15, Math.max(12, map.getZoom() + 8));
    window.open(viewerUrlArea(e.latlng.lat, e.latlng.lng, z), "_blank");
  });
  // flip blobs↔bboxes + viewport-cull the boxes as the map moves
  map.on("moveend zoomend", () => renderMap());
}

// At/above this zoom the map draws each layer's actual BOUNDING BOX (rectangles)
// for whatever's in view; below it, cheap binned density blobs. Re-renders on
// pan/zoom so it flips modes + viewport-culls the boxes.
const BBOX_ZOOM = 7;
let lastFiltered: Row[] = [];

function renderMap(rows?: Row[]) {
  if (rows) lastFiltered = rows;
  rows = lastFiltered;
  dotLayer.clearLayers();
  const color = KIND_COLOR[tab];
  if (map.getZoom() >= BBOX_ZOOM) {
    // bounding-box rectangles for layers intersecting the current view (capped)
    const view = map.getBounds();
    let n = 0;
    for (const r of rows) {
      const b = r.layer.bounds as [[number, number], [number, number]];
      const lb = L.latLngBounds(b[0], b[1]);
      if (!view.intersects(lb)) continue;
      if (++n > 800) break; // cap DOM/vector churn on dense areas (e.g. topo tiles)
      L.rectangle(lb, {
        color, weight: 1, opacity: 0.7, fillColor: color, fillOpacity: 0.06,
        interactive: false, // map click still opens the viewer at that spot
      }).addTo(dotLayer);
    }
    return;
  }
  // binned density blobs (whole-country overview)
  const canvas = L.canvas({ padding: 0.5 });
  const CELL = 0.18;
  const bins = new Map<string, { lat: number; lng: number; n: number }>();
  for (const r of rows) {
    const gy = Math.round(r.lat / CELL), gx = Math.round(r.lng / CELL);
    const key = gy + "," + gx;
    let b = bins.get(key);
    if (!b) { b = { lat: gy * CELL, lng: gx * CELL, n: 0 }; bins.set(key, b); }
    b.n++;
  }
  for (const b of bins.values()) {
    L.circleMarker([b.lat, b.lng], {
      renderer: canvas,
      radius: Math.min(13, 3 + Math.sqrt(b.n) * 1.6),
      color, weight: 0, fillColor: color, fillOpacity: 0.32,
      interactive: false,
    }).addTo(dotLayer);
  }
}

// ---- filtering ----
function currentRows(): Row[] {
  const q = search.trim().toLowerCase();
  const terms = q ? q.split(/\s+/) : [];
  const out = ROWS.filter((r) => {
    if (r.kind !== tab) return false;
    if (yearMin != null && (r.year == null || r.year < yearMin)) return false;
    if (yearMax != null && (r.year == null || r.year > yearMax)) return false;
    for (const t of terms) if (!r.hay.includes(t)) return false;
    return true;
  });
  out.sort((a, b) => {
    let av: number | string, bv: number | string;
    if (sortKey === "title") { av = a.hay; bv = b.hay; }
    else if (sortKey === "year") { av = a.year ?? -Infinity; bv = b.year ?? -Infinity; }
    else { av = a.resM ?? Infinity; bv = b.resM ?? Infinity; } // finer (smaller) first when asc
    if (av < bv) return -sortDir;
    if (av > bv) return sortDir;
    return 0;
  });
  return out;
}

// ---- rendering ----
function renderTabs() {
  const counts: Record<Kind, number> = { aerial: 0, map: 0, topo: 0 };
  for (const r of ROWS) counts[r.kind]++;
  const el = document.getElementById("tabs")!;
  el.innerHTML = "";
  (["aerial", "map", "topo"] as Kind[]).forEach((k) => {
    const b = document.createElement("button");
    b.className = "tab " + k + (k === tab ? " active" : "");
    b.innerHTML = `${KIND_LABEL[k]} <span class="n">${counts[k].toLocaleString()}</span>`;
    b.onclick = () => { tab = k; render(); };
    el.appendChild(b);
  });
}

function renderControls() {
  const el = document.getElementById("controls")!;
  el.innerHTML = "";
  const searchEl = Object.assign(document.createElement("input"), {
    type: "search", placeholder: "Filter by name / place…", value: search,
  });
  searchEl.oninput = () => { search = searchEl.value; renderRowsAndDots(); };
  const yMin = Object.assign(document.createElement("input"), { type: "number", className: "yr", placeholder: "from", value: yearMin ?? "" });
  const yMax = Object.assign(document.createElement("input"), { type: "number", className: "yr", placeholder: "to", value: yearMax ?? "" });
  yMin.oninput = () => { yearMin = yMin.value ? +yMin.value : null; renderRowsAndDots(); };
  yMax.oninput = () => { yearMax = yMax.value ? +yMax.value : null; renderRowsAndDots(); };
  const sortEl = document.createElement("select");
  [["year", "Newest first"], ["title", "Name A→Z"], ["resM", "Finest resolution"]].forEach(([v, lbl]) => {
    const o = document.createElement("option"); o.value = v; o.textContent = lbl; sortEl.appendChild(o);
  });
  sortEl.value = sortKey;
  sortEl.onchange = () => {
    sortKey = sortEl.value as typeof sortKey;
    sortDir = sortKey === "title" ? 1 : sortKey === "resM" ? 1 : -1;
    renderRowsAndDots();
  };
  const yr = document.createElement("label"); yr.textContent = "Year";
  el.append(searchEl, yr, yMin, yMax, sortEl);
}

function renderRowsAndDots() {
  const rows = currentRows();
  renderMap(rows);
  const box = document.getElementById("rows")!;
  const shown = rows.slice(0, ROW_CAP);
  const th = (k: typeof sortKey, lbl: string) =>
    `<th data-k="${k}">${lbl}${sortKey === k ? ` <span class="arr">${sortDir < 0 ? "▼" : "▲"}</span>` : ""}</th>`;
  const body = shown.map((r) => `
    <tr>
      <td><span class="kind-pill" style="background:${KIND_COLOR[r.kind]}"></span><a class="title" href="${viewerUrlLayer(r)}" target="_blank" rel="noopener">${escapeHtml(String(r.layer.title ?? r.layer.id))}</a></td>
      <td class="yr">${r.year ?? "—"}</td>
      <td class="res">${fmtRes(r.resM)}</td>
    </tr>`).join("");
  box.innerHTML = `<table><thead><tr>${th("title", "Layer")}${th("year", "Year")}${th("resM", "Resolution")}</tr></thead><tbody>${body}</tbody></table>`;
  box.querySelectorAll<HTMLElement>("thead th").forEach((h) => {
    h.onclick = () => {
      const k = h.dataset.k as typeof sortKey;
      if (sortKey === k) sortDir = (sortDir * -1) as 1 | -1;
      else { sortKey = k; sortDir = k === "title" ? 1 : k === "resM" ? 1 : -1; }
      renderRowsAndDots();
    };
  });
  document.getElementById("foot")!.textContent =
    rows.length > ROW_CAP
      ? `Showing ${ROW_CAP.toLocaleString()} of ${rows.length.toLocaleString()} — refine the filter to see the rest.`
      : `${rows.length.toLocaleString()} layer${rows.length === 1 ? "" : "s"}.`;
}

function render() {
  renderTabs();
  renderControls();
  renderRowsAndDots();
}

function renderLegend() {
  document.getElementById("legend")!.innerHTML = (["aerial", "map", "topo"] as Kind[])
    .map((k) => `<div class="row"><span class="dot" style="background:${KIND_COLOR[k]}"></span>${KIND_LABEL[k]}</div>`)
    .join("");
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

async function main() {
  buildMap();
  renderLegend();
  const cat = await loadCatalog();
  for (const l of cat.layers) {
    const kind = classify(l);
    if (!kind || !l.bounds) continue;
    const [[s, w], [n, e]] = l.bounds;
    const lat = (s + n) / 2, lng = (w + e) / 2;
    ROWS.push({
      layer: l, kind, lat, lng,
      year: typeof l.year === "number" ? l.year : null,
      resM: resMeters(l, lat),
      fitZoom: fitZoom(l.bounds),
      hay: String(l.title ?? l.id).toLowerCase(),
    });
  }
  document.getElementById("total-count")!.textContent = `${ROWS.length.toLocaleString()} layers`;
  render();
}

main().catch((err) => {
  document.getElementById("rows")!.innerHTML =
    `<div class="loading">Failed to load catalog: ${escapeHtml(String(err))}</div>`;
});
